import React, { useCallback, useEffect, useState } from "react";
import { fmtTime, fmtRange } from "@/lib/enrol";
import { api, useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Users, Clock, MapPin, CalendarDays, X, Plus, Trash2, UserPlus, Ticket, PartyPopper, AlertOctagon, Check } from "lucide-react";
import { useConfirm } from "@/lib/confirm";

const DAY_LABELS = { Monday: "MON", Tuesday: "TUE", Wednesday: "WED", Thursday: "THU", Friday: "FRI", Saturday: "SAT", Sunday: "SUN" };

export default function MyClasses() {
  const { user } = useAuth();
  const [schedule, setSchedule] = useState(null);
  const [programs, setPrograms] = useState(null);
  const [coverage, setCoverage] = useState([]);
  const [loading, setLoading] = useState(true);
  const [attendeeModal, setAttendeeModal] = useState(null);
  const [coverModal, setCoverModal] = useState(null); // session for which to request cover

  const load = useCallback(async () => {
    try {
      const [s, p, c] = await Promise.all([
        api.get("/admin/my-weekly-schedule"),
        api.get("/admin/programs"),
        api.get("/admin/coverage-requests"),
      ]);
      setSchedule(s.data);
      setPrograms(p.data);
      setCoverage(c.data || []);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="p-4 sm:p-8 text-zinc-500">Loading your schedule…</div>;

  const total = schedule?.total_sessions || 0;
  const wsUpcoming = programs?.workshops_upcoming || [];
  const evUpcoming = programs?.events_upcoming || [];

  return (
    <div className="p-4 sm:p-8 max-w-[1600px]">
      <div className="mb-8">
        <div className="eyebrow mb-2 flex items-center gap-2"><CalendarDays size={12}/>Your weekly schedule</div>
        <h1 className="font-display text-5xl uppercase">My Classes</h1>
        <p className="text-zinc-400 mt-2 text-sm">
          {total === 0
            ? "You haven't been assigned any classes yet. Once a manager assigns you as the instructor, they'll appear here."
            : <>You're the instructor on <strong className="text-white">{total}</strong> {total === 1 ? "class" : "classes"} across the week. Below that: every event &amp; workshop the school runs — so anyone can drop in a manual attendee.</>}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-7 gap-3" data-testid="weekly-grid">
        {(schedule?.days || []).map(({ day, sessions }) => (
          <div key={day} className="card-surface p-4" data-testid={`day-${day}`}>
            <div className="flex items-baseline justify-between mb-3">
              <div className="font-display text-lg tracking-widest">{DAY_LABELS[day] || day}</div>
              <div className="text-[10px] text-zinc-500">{sessions.length} {sessions.length === 1 ? "class" : "classes"}</div>
            </div>
            <div className="space-y-3">
              {sessions.length === 0 && <div className="text-zinc-600 text-xs italic">— rest day —</div>}
              {sessions.map(s => (
                <div key={s.id} className="border-l-2 border-red-600 pl-3 py-2 group" data-testid={`schedule-session-${s.id}`}>
                  <div className="font-bold text-sm text-white">{s.batch_name || "Class"}</div>
                  <div className="text-zinc-400 text-xs flex items-center gap-1 mt-1"><Clock size={10}/>{fmtRange(s.start_time, s.end_time)}</div>
                  <div className="text-zinc-400 text-xs flex items-center gap-1 mt-1"><MapPin size={10}/>{s.location_name || "—"}</div>
                  <div className="text-zinc-500 text-[10px] flex items-center gap-1 mt-1"><Users size={10}/>{s.registration_count ?? 0}/{s.capacity ?? 20} enrolled</div>
                  <div className="mt-2 flex items-center gap-3 flex-wrap">
                    <button onClick={() => setAttendeeModal({ kind: "class", item: s })} className="text-[10px] uppercase tracking-widest text-red-400 hover:text-red-300 flex items-center gap-1" data-testid={`manage-class-${s.id}`}>
                      <UserPlus size={10}/>Walk-in
                    </button>
                    <button onClick={() => setCoverModal(s)} className="text-[10px] uppercase tracking-widest text-amber-400 hover:text-amber-300 flex items-center gap-1" data-testid={`need-cover-${s.id}`}>
                      <AlertOctagon size={10}/>Need cover
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Coverage requests */}
      {coverage.length > 0 && (
        <div className="mt-12" data-testid="coverage-list">
          <div className="eyebrow mb-3 flex items-center gap-2"><AlertOctagon size={12}/>Coverage Requests ({coverage.length})</div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {coverage.map(r => {
              const mine = r.requested_by_admin_id === user?.id;
              return (
                <div key={r.id} className="card-surface p-4" data-testid={`cover-${r.id}`}>
                  <div className="font-bold text-white">{r.session_snapshot?.batch_name || "Class"}</div>
                  <div className="text-zinc-400 text-xs mt-1">{r.session_snapshot?.day} · {fmtRange(r.session_snapshot?.start_time, r.session_snapshot?.end_time)}</div>
                  <div className="text-zinc-500 text-xs">{r.session_snapshot?.location_name || "—"}</div>
                  <div className="text-zinc-500 text-xs mt-2">Requested by: <span className="text-zinc-300">{r.requested_by_name || "—"}</span></div>
                  {r.reason && <div className="text-zinc-500 text-[11px] mt-1 italic">"{r.reason}"</div>}
                  <div className="mt-3">
                    {mine ? (
                      <button onClick={async () => { try { await api.post(`/admin/coverage-requests/${r.id}/cancel`); toast.success("Cancelled"); load(); } catch (e) { toast.error("Failed"); } }} className="btn-ghost !py-1 !px-2 !text-[10px]" data-testid={`cancel-cover-${r.id}`}>Cancel</button>
                    ) : (
                      <button onClick={async () => { try { await api.post(`/admin/coverage-requests/${r.id}/accept`); toast.success("Accepted — added to your payroll"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } }} className="btn-red !py-1 !px-2 !text-[10px] flex items-center gap-1" data-testid={`accept-cover-${r.id}`}>
                        <Check size={12}/>Accept cover shift
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Shared programs */}
      <div className="mt-12 space-y-8">
        <ProgramList
          title="Upcoming Events"
          icon={PartyPopper}
          testId="events-list"
          items={evUpcoming}
          renderMeta={e => `${e.date || "date TBA"} · ${e.location || "location TBA"}`}
          renderName={e => e.caption || "Untitled event"}
          onManage={e => setAttendeeModal({ kind: "event", item: e })}
        />
        <ProgramList
          title="Upcoming Workshops"
          icon={Ticket}
          testId="workshops-list"
          items={wsUpcoming}
          renderMeta={w => `${w.date || "date TBA"} · ${w.location_name || "location TBA"} · Instructor: ${w.instructor || "—"}`}
          renderName={w => w.name || "Untitled workshop"}
          onManage={w => setAttendeeModal({ kind: "workshop", item: w })}
        />
      </div>

      {attendeeModal && (
        <AttendeeModal
          kind={attendeeModal.kind}
          item={attendeeModal.item}
          onClose={() => setAttendeeModal(null)}
          onChange={load}
        />
      )}
      {coverModal && <CoverRequestModal session={coverModal} onClose={() => setCoverModal(null)} onDone={() => { setCoverModal(null); load(); }} />}
    </div>
  );
}

function CoverRequestModal({ session, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [date, setDate] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try {
      const r = await api.post(`/admin/sessions/${session.id}/request-cover`, { reason: reason || null, date: date || null });
      toast.success(`Cover request sent — ${r.data.matches} teammate(s) notified`);
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/80 z-50 grid place-items-center p-6" onClick={onClose}>
      <div className="card-surface p-6 max-w-md w-full" onClick={e => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-display text-2xl uppercase">Need cover</h3>
          <button onClick={onClose}><X size={18}/></button>
        </div>
        <div className="text-zinc-400 text-sm mb-2">{session.batch_name}</div>
        <div className="text-zinc-500 text-xs mb-4">{session.day} · {fmtRange(session.start_time, session.end_time)} · {session.location_name}</div>
        <div className="grid grid-cols-1 gap-3">
          <div>
            <label className="eyebrow block mb-1">Date needing cover (optional)</label>
            <input type="date" value={date} onChange={e => setDate(e.target.value)} className="w-full bg-black border border-white/10 px-3 py-2 text-sm focus:border-red-600 outline-none" data-testid="cover-date"/>
          </div>
          <div>
            <label className="eyebrow block mb-1">Reason (optional)</label>
            <textarea value={reason} onChange={e => setReason(e.target.value)} rows={3} placeholder="Away, sick, etc." className="w-full bg-black border border-white/10 px-3 py-2 text-sm focus:border-red-600 outline-none" data-testid="cover-reason"/>
          </div>
        </div>
        <p className="text-zinc-500 text-[11px] mt-3">Only teammates whose availability matches this day &amp; time will be emailed.</p>
        <button onClick={submit} disabled={saving} className="btn-red w-full mt-4" data-testid="cover-submit">
          {saving ? "Sending…" : "Send cover request"}
        </button>
      </div>
    </div>
  );
}

function ProgramList({ title, icon: Icon, items, renderName, renderMeta, onManage, testId }) {
  return (
    <div data-testid={testId}>
      <div className="eyebrow mb-3 flex items-center gap-2"><Icon size={12}/>{title} <span className="text-zinc-600">({items.length})</span></div>
      {items.length === 0 ? (
        <div className="text-zinc-600 text-xs italic">— none scheduled —</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {items.map(it => (
            <div key={it.id} className="card-surface p-4 flex flex-col" data-testid={`${testId}-item-${it.id}`}>
              <div className="font-bold text-white">{renderName(it)}</div>
              <div className="text-zinc-400 text-xs mt-1">{renderMeta(it)}</div>
              <div className="mt-3 flex items-center justify-between">
                <div className="text-zinc-500 text-[10px] flex items-center gap-1"><Users size={10}/>{it.registration_count ?? 0}{it.capacity ? `/${it.capacity}` : ""} registered</div>
                <button onClick={() => onManage(it)} className="btn-red !py-1 !px-2 !text-[10px] flex items-center gap-1" data-testid={`manage-${it.id}`}><UserPlus size={12}/>Manage</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AttendeeModal({ kind, item, onClose, onChange }) {
  const isEvent = kind === "event";
  const isClass = kind === "class";
  const base = isEvent
    ? `/admin/events/${item.id}`
    : isClass
    ? `/admin/sessions/${item.id}`
    : `/admin/workshops/${item.id}`;
  const listPrice = Number(item.price ?? 0);
  const [attendees, setAttendees] = useState(null);
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", notes: "", amount: listPrice > 0 ? String(listPrice) : "", payment_status: listPrice > 0 ? "paid" : "manual" });
  const [saving, setSaving] = useState(false);
  const confirm = useConfirm();

  const loadList = useCallback(async () => {
    try { const r = await api.get(`${base}/registrations`); setAttendees(r.data || []); }
    catch { setAttendees([]); }
  }, [base]);
  useEffect(() => { loadList(); }, [loadList]);

  const submit = async () => {
    if (!form.full_name.trim()) { toast.error("Attendee name is required"); return; }
    setSaving(true);
    try {
      const payload = {
        full_name: form.full_name, email: form.email, phone: form.phone, notes: form.notes,
        amount: form.amount === "" ? 0 : Number(form.amount),
        payment_status: form.payment_status,
      };
      await api.post(`${base}/manual-register`, payload);
      toast.success(`${form.full_name} added`);
      setForm({ full_name: "", email: "", phone: "", notes: "", amount: listPrice > 0 ? String(listPrice) : "", payment_status: listPrice > 0 ? "paid" : "manual" });
      await loadList(); onChange && onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const remove = async (rid, name) => {
    const ok = await confirm({ title: `Remove ${name}?`, description: "They'll be dropped from the roster and the count will go down.", danger: true, confirmLabel: "Remove" });
    if (!ok) return;
    try {
      await api.delete(`${base}/registrations/${rid}`);
      toast.success("Removed");
      await loadList(); onChange && onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const title = isEvent ? (item.caption || "Event") : isClass ? (item.batch_name || "Class") : (item.name || "Workshop");
  const kindLabel = isEvent ? "Event" : isClass ? "Class" : "Workshop";
  const whenLabel = isClass
    ? `${item.day || ""} · ${fmtRange(item.start_time, item.end_time)}`
    : (item.date || "date TBA");
  const whereLabel = isClass ? (item.location_name || "") : isEvent ? (item.location || "") : (item.location_name || "");
  return (
    <div className="fixed inset-0 bg-black/80 z-50 grid place-items-center p-6 overflow-y-auto" onClick={onClose}>
      <div className="card-surface p-6 max-w-2xl w-full my-8" onClick={e => e.stopPropagation()}>
        <div className="flex justify-between items-start mb-2">
          <div>
            <div className="eyebrow mb-1">{kindLabel} · {whenLabel}{listPrice > 0 && <span className="ml-2 text-white">· ${listPrice.toFixed(2)} list price</span>}</div>
            <h3 className="font-display text-2xl uppercase">{title}</h3>
          </div>
          <button onClick={onClose}><X size={20}/></button>
        </div>
        <p className="text-zinc-500 text-xs mb-6">{whereLabel} {isEvent ? "" : (item.instructor ? `· Instructor: ${item.instructor}` : "")}</p>

        {/* Add attendee */}
        <div className="border border-red-600/30 bg-red-600/5 p-4 mb-6">
          <div className="eyebrow mb-3 flex items-center gap-2"><UserPlus size={12}/>Add attendee manually</div>
          <div className="grid grid-cols-2 gap-2">
            <input placeholder="Full name *" value={form.full_name} onChange={e => setForm({...form, full_name: e.target.value})} className="col-span-2 bg-black border border-white/10 px-3 py-2 text-sm focus:border-red-600 outline-none" data-testid="attendee-name"/>
            <input placeholder="Email (optional)" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="bg-black border border-white/10 px-3 py-2 text-sm focus:border-red-600 outline-none" data-testid="attendee-email"/>
            <input placeholder="Phone (optional)" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} className="bg-black border border-white/10 px-3 py-2 text-sm focus:border-red-600 outline-none" data-testid="attendee-phone"/>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-zinc-500 block mb-1">Amount paid ($)</label>
              <input type="number" step="0.01" value={form.amount} onChange={e => setForm({...form, amount: e.target.value})} placeholder={listPrice > 0 ? `List: $${listPrice.toFixed(2)}` : "0.00"} className="w-full bg-black border border-white/10 px-3 py-2 text-sm focus:border-red-600 outline-none" data-testid="attendee-amount"/>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-zinc-500 block mb-1">Payment status</label>
              <select value={form.payment_status} onChange={e => setForm({...form, payment_status: e.target.value})} className="w-full bg-black border border-white/10 px-3 py-2 text-sm focus:border-red-600 outline-none" data-testid="attendee-status">
                <option value="paid">Paid</option>
                <option value="pending">Pending</option>
                <option value="manual">Manual</option>
                <option value="comp">Comp / free</option>
              </select>
            </div>
            <input placeholder="Notes (optional)" value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} className="col-span-2 bg-black border border-white/10 px-3 py-2 text-sm focus:border-red-600 outline-none" data-testid="attendee-notes"/>
          </div>
          <button onClick={submit} disabled={saving || !form.full_name.trim()} className="btn-red mt-3 !py-2 !px-4 !text-xs flex items-center gap-1" data-testid="attendee-submit">
            <Plus size={12}/>{saving ? "Adding…" : "Add attendee"}
          </button>
        </div>

        {/* Roster */}
        <div className="eyebrow mb-3 flex items-center gap-2"><Users size={12}/>Roster ({attendees?.length ?? 0})</div>
        <div className="max-h-[35vh] overflow-y-auto space-y-2 pr-1">
          {attendees === null && <div className="text-zinc-500 text-xs">Loading…</div>}
          {attendees && attendees.length === 0 && <div className="text-zinc-500 text-xs italic">No one registered yet.</div>}
          {(attendees || []).map(a => (
            <div key={a.id} className="border border-white/5 p-3 flex items-center justify-between text-sm" data-testid={`attendee-row-${a.id}`}>
              <div>
                <div className="text-white">{a.full_name} {a.manual_entry && <span className="text-[9px] uppercase tracking-widest text-red-400 ml-2">manual</span>}</div>
                <div className="text-zinc-500 text-xs">
                  {a.email || "no email"} · {a.phone || "no phone"} · <span className="text-emerald-400">{a.payment_status || "—"}</span>
                  {a.amount !== undefined && <span className="text-zinc-400 ml-2">· ${Number(a.amount ?? 0).toFixed(2)}</span>}
                </div>
                {a.notes && <div className="text-zinc-500 text-[11px] mt-1">{a.notes}</div>}
              </div>
              <button onClick={() => remove(a.id, a.full_name)} className="text-zinc-500 hover:text-red-500 p-1" data-testid={`attendee-del-${a.id}`}><Trash2 size={14}/></button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
